import { useQuery } from '@tanstack/react-query';
import { HealthScoreEquipments } from 'components/DeviationToPdf/types';
import { HEALTH_TREE, REPORTS_ASSETS_HEALTH, REPORTS_PDF } from 'config/endpoints';
import { ActionStatus, AssetConditionEvaluation } from 'queries/assets/contract';
import { GenericQueryOptions } from 'queries/types/asset';
import { get } from 'utils/axios';
import { ReportAssets, ReportHealth, ReportPDFReponse } from './contract';
import { LOCAL_STORAGE_WORKSPACE_ID } from 'config/constants';

export const useReportPDFQuery = (
  { assetId, month, year }: { assetId?: string; month?: string; year?: string },
  queryOptions?: GenericQueryOptions<ReportPDFReponse>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(
    ['REPORT_PDF', 'PDF_LOADING', assetId, month, year],
    get<ReportPDFReponse>(REPORTS_PDF(assetId, month, year, workspaceId)),
    { enabled: !!assetId && !!month && !!year, ...queryOptions },
  );
};

export const useReportAssetsWithHealthQuery = (
  { assetId, start, end }: { assetId?: string; start?: string; end?: string },
  queryOptions?: GenericQueryOptions<HealthScoreEquipments>,
) => {
  return useQuery(
    ['REPORTS_ASSETS', 'RERT_LOADING', assetId, start, end],
    get<HealthScoreEquipments>(REPORTS_ASSETS_HEALTH(assetId, start, end)),
    { enabled: !!assetId && !!start && !!end, ...queryOptions },
  );
};
export const useRERTHealthTreeQuery = (
  { month, year }: { month: number; year: number },
  queryOptions?: GenericQueryOptions<{ values: ReportHealth[] }>,
) => {
  return useQuery(
    ['RERT', 'REPORT_ASSETS_HEALTH', 'RERT_LOADING', month, year],
    get<{ values: ReportHealth[] }>(HEALTH_TREE(month, year)),
    { enabled: !!month, ...queryOptions },
  );
};

const data: { assets: ReportAssets[] } = {
  assets: [
    {
      id: 'MV18',
      name: 'FPSO Cidade de Niteroi',
      health_score: 98.6,
      condition_deviations: [
        {
          id: 'deviation-1',
          asset: { id: 'MV18 filho', name: 'filho FPSO Cidade de Niteroi' },
          dimension: 'Pressure',
          deviation_start: '2025-01-10T08:00:00Z',
          deviation_end: null,
          last_update_date: '2025-05-01T12:00:00Z',
          action_status: ActionStatus.INVESTIGATING,
          condition_evaluation: AssetConditionEvaluation.CRITICAL_CONDITION,
          diagnosis: 'Pressure levels exceeded safe thresholds',
          recommended_action: 'Inspect pressure valves and adjust settings',
          sap_note: '789012',
          sap_status: 'OPEN-CRITICAL',
          detection_method: 'Automated monitoring',
          is_out_of_service: false,
          vessel: 'FPSO Cidade de Niteroi',
          detailing: 'Pressure levels remain critical for over 48 hours.',
          attachments: [],
          savings_required: true,
        },
      ],
      subitems: [
        {
          id: 'GasCompressionid',
          name: 'Gas Compression',
          health_score: 100.0,
          condition_deviations: [
            {
              id: 'deviation-2',
              asset: { id: 'GasCompression filho', name: 'filho Gas Compression' },
              dimension: 'Temperature',
              deviation_start: '2025-02-01T10:00:00Z',
              deviation_end: '2025-02-05T18:00:00Z',
              last_update_date: '2025-02-05T18:00:00Z',
              action_status: ActionStatus.COMPLETED,
              condition_evaluation: AssetConditionEvaluation.NORMAL_CONDITION,
              diagnosis: 'Temperature spike resolved after maintenance',
              recommended_action: 'Monitor periodically',
              sap_note: '654321',
              sap_status: 'CLOSED',
              detection_method: 'Manual inspection',
              is_out_of_service: false,
              vessel: 'FPSO Cidade de Niteroi',
              detailing: 'Temperature returned to normal levels.',
              attachments: [],
              savings_required: false,
            },
          ],
          subitems: [
            {
              id: 'HPGasCompressionid',
              name: 'HP Gas Compression',
              health_score: 100.0,
              condition_deviations: [
                {
                  id: 'deviation-3',
                  asset: { id: 'HPGasCompression filho', name: 'flho HP Gas Compression' },
                  dimension: 'Vibration',
                  deviation_start: '2025-03-01T12:00:00Z',
                  deviation_end: null,
                  last_update_date: '2025-05-01T12:00:00Z',
                  action_status: ActionStatus.INVESTIGATING,
                  condition_evaluation: AssetConditionEvaluation.ALARM_CONDITION,
                  diagnosis: 'High vibration levels detected',
                  recommended_action: 'Inspect bearings and perform maintenance',
                  sap_note: '123456',
                  sap_status: 'OPEN-CRITICAL',
                  detection_method: 'Sensor analysis',
                  is_out_of_service: false,
                  vessel: 'FPSO Cidade de Niteroi',
                  detailing: 'Vibration levels exceeded acceptable thresholds.',
                  attachments: [],
                  savings_required: true,
                },
              ],
              subitems: [],
            },
          ],
        },
      ],
    },
  ],
};
